import { Component, Input, OnInit } from '@angular/core';
import {
    IDetailPriceTicket,
    IFareComponentTicket,
    IRuleTicket,
    ITicketSector,
    IXId,
    sourceTicketBooking
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { Yii } from 'libraries/interfaces/Yii';
import { ILabels } from 'libraries/interfaces/ILabels';
import { ILuggageRestriction, RestrictionUnit } from 'libraries/service/resources/model/booking/air/IAirPrice';
import { wenji } from "libraries/interfaces/IWenji";

@Component({
    selector: 'sector-air-flight-details',
    template: `
        <div class="flight-details-container" *ngIf="sectorData">
            <airport-details class="flight-details-column" [sectorData]="sectorData"></airport-details>
            <div class="flight-details-details flight-details-column">
                <div class="detail-data-group">
                    <div *ngIf="sectorData.carrier">
                        {{ labels.flight }}
                        <span *ngIf="sectorData.carrier.code"
                              [class.booking-source]="carrierBookingSource"
                              class="carrier-code bolded-information">
                            {{sectorData.carrier.code}}
                        </span>
                        <ng-container *ngIf="sectorData.carrier.iata">
                            <span *ngIf="!sectorData.carrier.code || sectorData.carrier.code !== sectorData.carrier.iata"
                                  [class.booking-source]="carrierBookingSource"
                                  class="carrier-iata bolded-information">
                                {{sectorData.carrier.iata}}
                            </span>
                        </ng-container>

                        <span *ngIf="sectorData.flight"
                              [class.booking-source]="carrierBookingSource"
                              class="flight bolded-information">{{ sectorData.flight }}
                        </span>
                        <span *ngIf="sectorData.carrier.name" [class.booking-source]="carrierBookingSource">({{sectorData.carrier.name | localized}})</span>
                    </div>
                    <div *ngIf="urlAirLogo && sectorData?.carrier?.code" >
                        <img class="air-logo"
                             [attr.src]="urlAirLogo + sectorData.carrier?.code + '.png'"
                             [hidden]="!showImage"
                             [attr.alt]="sectorData.carrier.code"
                             [title]="sectorData.carrier.name"
                             (error)="showImage = false"
                             (load)="showImage = true">
                    </div>
                    

                    <div *ngIf="!sectorData.carrier">
                        {{ labels.flight }} <span *ngIf="sectorData.operatedBy?.carrier?.code"
                                                  class="bolded-information">{{sectorData.operatedBy.carrier.code}} </span>
                        <span [class.booking-source]="carrierBookingSource" class="bolded-information">{{ sectorData.flight }} </span>
                        <span *ngIf="sectorData.operatedBy?.carrier?.name">({{sectorData.operatedBy.carrier.name}})</span>
                    </div>
                    
                    <div *ngIf="sectorData.operatedBy">
                        {{ labels.operatedBy }}
                        <span *ngIf="sectorData.operatedBy?.carrier?.name">{{sectorData.operatedBy.carrier.name}} </span>
                        <span *ngIf="sectorData.operatedBy?.carrier?.code">({{sectorData.operatedBy.carrier.code}})</span>
                    </div>

                    <div *ngIf="sectorData.aircraft">
                        <span>{{ labels.aircraft }}</span><span class="bolded-information"
                                                                [class.booking-source]="aircraftBookingSource"> {{ sectorData.aircraft.name }}</span>
                    </div>

                    <div *ngIf="sectorData.seat?.cabin">
                        <span>{{ labels.cabin }}</span> <span class="bolded-information" [class.booking-source]="seatBookingSource">
                    {{cabin}}
                </span>
                    </div>

                    <div *ngIf="fareComponent?.class">
                        <span>{{ labels.class }}</span> <span class="bolded-information">{{ fareComponent.class }}</span> <span>({{ labels.farebasis }} <span
                            class="bolded-information">{{ fareComponent.fareBasis.code }}</span>)</span>
                    </div>

                    <div *ngIf="!fareComponent && sectorData?.seat?.code">
                        <span>{{ labels.class }}</span> <span class="bolded-information">{{ sectorData.seat.code }}</span>
                    </div>

                    <div *ngIf="rule?.luggage && luggageRestriction">
                        <luggage [restriction]="luggageRestriction"
                                 [type]="rule.luggage.xCategory"
                                 [isQuantityBolded]="true">
                        </luggage>
                    </div>
                </div>

                <div *ngIf="showCarbonFootprint">
                    <carbon-footprint [carbonValue]="sectorData.carbonFootprint"></carbon-footprint>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .flight-details-container {
            padding: .75rem .75rem 0;
            display: flex;
            flex-wrap: wrap;
        }

        .flight-details-column {
            margin-bottom: .75rem;
            width: 300px;
        }

        airport-details {
            flex: 2;
        }

        .flight-details-details {
            flex: 1;
        }

        em {
            font-size: 0.9rem;
        }

        .detail-data-group {
            margin-bottom: 0.3rem;
        }

        .undefined-data {
            color: #707070;
        }

        .booking-source {
            font-style: italic;
        }

        .bolded-information {
            font-weight: bold;
        }
    `]
})

export class SectorAirFlightDetailsComponent implements OnInit {
    @Input()
    public sectorData: ITicketSector;
    @Input()
    public detail: IDetailPriceTicket;
    @Input()
    public showCarbonFootprint: boolean;

    public labels: ILabels = {
        aircraft: Yii.t('wui-shared-js', "Appareil"),
        externalPNR: Yii.t('wui-shared-js', "PNR externe"),
        cabin: Yii.t('wui-shared-js', "Cabine"),
        class: Yii.t('wui-shared-js', "Classe"),
        farebasis: Yii.t('wui-shared-js', "Farebasis"),
        carbon: Yii.t('wui-shared-js', "CO²"),
        flight: Yii.t('wui-shared-js', "Vol n°"),
        PNRUndefined: Yii.t('wui-shared-js', "Non défini"),
        operatedBy: Yii.t('wui-shared-js', 'Opéré par '),
        luggageQuantityMax: Yii.t('wui-shared-js', "Bagages maximum"),
    };

    public seatBookingSource: boolean;
    public aircraftBookingSource: boolean;
    public carrierBookingSource: boolean;

    public rule: IRuleTicket;
    public fareComponent: IFareComponentTicket;
    public luggageRestriction: ILuggageRestriction[] = [];
    public cabin: string;

    public urlAirLogo: string;
    public showImage: boolean;

    public ngOnInit(): void {
        this.seatBookingSource = Boolean(
            this.sectorData.seat &&
            this.sectorData.seat.source &&
            this.sectorData.seat.source === sourceTicketBooking
        );
        this.aircraftBookingSource = Boolean(
            this.sectorData.aircraft &&
            this.sectorData.aircraft.source &&
            this.sectorData.aircraft.source === sourceTicketBooking
        );
        this.carrierBookingSource = Boolean(
            this.sectorData.carrier &&
            this.sectorData.carrier.source &&
            this.sectorData.carrier.source === sourceTicketBooking
        );

        this.getLuggageAndFareBasisInformation();

        if (this.sectorData && this.sectorData.seat && this.sectorData.seat.cabin) {
            this.cabin = this.sectorData.seat.cabin[0].toUpperCase() + this.sectorData.seat.cabin.slice(1);
        }

        if (wenji.locobook && wenji.locobook.carrierLogos) {
            this.urlAirLogo = wenji.locobook.carrierLogos;
        }
    }

    private getLuggageAndFareBasisInformation(): void {
        if (this.detail && this.detail.rate) {
            this.detail.rate.ruleArray && this.detail.rate.ruleArray.forEach((ruleTicket: IRuleTicket) => {
                if (ruleTicket.sectorArray.find((sector: IXId) => sector.xRef === this.sectorData.xId)) {
                    this.rule = ruleTicket;
                    this.getLuggageRestriction();
                }
            });
            this.detail.rate.fareComponentArray && this.detail.rate.fareComponentArray.forEach((fareComponentTicket: IFareComponentTicket) => {
                if (fareComponentTicket.sectorArray.find((sector: IXId) => sector.xRef === this.sectorData.xId)) {
                    this.fareComponent = fareComponentTicket;
                }
            });
        }
    }

    private getLuggageRestriction(): void {
        if (this.rule.luggage.weight) {
            this.luggageRestriction.push({
                max: this.rule.luggage.weight.max,
                unit: this.rule.luggage.weight.xUnit,
            });
        } else if (this.rule.luggage.quantity) {
            this.luggageRestriction.push({
                max: this.rule.luggage.quantity.max,
                unit: RestrictionUnit.luggage,
            });
        }
    }
}
